import { registerWidget, registerHintWidget, type CellContentProps } from '../registry'
import { NumberEditor } from './number'
import { CalculatedContent } from './formatted-number'

/**
 * FormattedStringContent — generic read cell for string-family types that have
 * no dedicated widget. Dispatch is by render_hint at registration time
 * (passed as a closure): "mono" → monospace class; "link" → muted link style;
 * "plain" → same as TextContent.
 *
 * Kinds: phone-number (mono), domain (link); calculated registers here with
 * the number family's UnitContent (SEN-226). None have a write editor in Phase 1.
 */
function makeStringReader(hint: 'mono' | 'link' | 'plain') {
  return function FormattedStringContent({ value }: CellContentProps) {
    const text = value == null ? '' : String(value)
    if (!text) return <span className="text-muted-foreground">—</span>
    if (hint === 'mono') {
      return <span className="font-mono text-sm">{text}</span>
    }
    if (hint === 'link') {
      return <span className="text-muted-foreground hover:text-foreground truncate">{text}</span>
    }
    // 'plain'
    return <>{text}</>
  }
}

registerWidget('phone-number', { read: makeStringReader('mono'), defaultWidth: 160 })
registerWidget('domain',       { read: makeStringReader('link'), defaultWidth: 200 })
// Calculated values arrive as raw floats from the read-time resolver
// (SEN-1395). SEN-226: they read like a measured number — the number
// family's unit cell, with decimals by magnitude (formatCalculatedValue) and
// the formula's declared unit (declaredUnit reads
// config.formula.unit_composition), so an EUI reads "95.3 kWh/m²".
// SEN-1784 known-gap: calculated declares numeric filter_operators, so a
// gt/lt condition needs a VALUE — filterOperand supplies the same numeric
// input the number kind uses, while cells stay read-only (no `write`).
// 150 px: the unit is part of the value now ("394 kWh/m²·year" clipped at 110).
registerWidget('calculated',   { read: CalculatedContent, filterOperand: NumberEditor, align: 'right', defaultWidth: 150,
                                 showsUnit: true })

registerHintWidget('mono',  { read: makeStringReader('mono'),  defaultWidth: 160 })
registerHintWidget('link',  { read: makeStringReader('link'),  defaultWidth: 200 })
registerHintWidget('plain', { read: makeStringReader('plain'), defaultWidth: 110 })
